"use client";

import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import React from "react";
import SliderPrincipale from "@/components/Home/SliderPrincipale";
import BrandGrid from "@/components/Home/BrandGrid";

// react-query + Redux store are ONLY needed for SearchAuto.
// Dynamic import (ssr: false) keeps them out of the critical-path bundle,
// saving ~720 KB compressed / 2.3 MB raw from the initial parse cost.
const DeferredSearchAuto = dynamic(
    () => import("@/components/Home/SearchAutoWithProviders"),
    {
        ssr: false,
        loading: () => <div className="h-[200px]" />,
    },
);

interface HomeClientProps {
    offers: any[];
    banners: any[];
    locations: any[];
    reviews: any[];
    brands: any[];
    searchAutoFilters: any;
    isB2B: boolean;
    useNewHomeSearch: boolean;
}

const DeferredLastMinute = dynamic(() => import("@/components/LastMinute"), {
    ssr: false,
    loading: () => <div className="min-h-[640px] bg-[#E1E7FF]" />,
});
const DeferredDoveSiamoSlide = dynamic(() => import("@/components/Home/Sedi"), {
    ssr: false,
    loading: () => <div className="min-h-[520px]" />,
});
const DeferredReview = dynamic(() => import("@/components/Review"), {
    ssr: false,
    loading: () => <div className="min-h-[520px] bg-[#F4F4F4]" />,
});

const B2BPage = React.memo(() => (
    <div className="w-full h-screen flex items-center justify-center bg-white">
        <Image
            src="/logo_consorzio.png"
            alt="Logo Consorzio"
            width={600}
            height={200}
            className="max-w-full h-auto"
            priority={true}
        />
    </div>
));

B2BPage.displayName = "B2BPage";

export default function HomeClient({
    offers,
    banners,
    locations,
    reviews,
    brands,
    searchAutoFilters,
    isB2B,
    useNewHomeSearch,
}: HomeClientProps) {
    const [showDeferredSections, setShowDeferredSections] = React.useState(false);

    React.useEffect(() => {
        if (window.location.hash !== "#cerca-veicolo") return;

        const scrollToVehicleSearch = () => {
            const searchSection = Array.from(
                document.querySelectorAll<HTMLElement>("[data-home-vehicle-search]"),
            ).find((element) => element.getClientRects().length > 0);

            searchSection?.scrollIntoView({ behavior: "smooth", block: "start" });
        };

        requestAnimationFrame(() => requestAnimationFrame(scrollToVehicleSearch));
    }, []);

    React.useEffect(() => {
        const activate = () => setShowDeferredSections(true);

        if ("requestIdleCallback" in globalThis) {
            const idleId = globalThis.requestIdleCallback(activate, { timeout: 1500 });
            return () => globalThis.cancelIdleCallback(idleId);
        }

        const timeoutId = globalThis.setTimeout(activate, 1200);
        return () => globalThis.clearTimeout(timeoutId);
    }, []);

    if (isB2B) {
        return <B2BPage />;
    }

    const schemaOrgJsonLD = {
        "@context": "http://schema.org",
        "@type": "AutoDealer",
        name: "Gruppo Piccirillo",
        image: "https://www.gruppopiccirillo.it/logo.png",
        "@id": "https://www.gruppopiccirillo.it",
        url: "https://www.gruppopiccirillo.it",
        telephone: "+39-0823847911",
        address: {
            "@type": "PostalAddress",
            streetAddress: "VIA SANTELLA PARCO LA PERLA SNC",
            addressLocality: "SANTA MARIA CAPUA VETERE",
            addressRegion: "CE",
            postalCode: "81055",
            addressCountry: "IT",
        },
        priceRange: "$$$",
        sameAs: [
            "https://www.facebook.com/gruppopiccirillo",
            "https://www.instagram.com/gruppopiccirillo",
        ],
    };

    return (
        <>
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaOrgJsonLD) }}
            />

            <div className="relative min-h-[70vh]">
                <SliderPrincipale banners={banners} />

                <div
                    className={`absolute inset-0 hidden md:flex items-start justify-end z-20 px-4 pb-6 pointer-events-none ${
                        useNewHomeSearch ? "pt-12 lg:pt-16" : "pt-24 lg:pt-28"
                    }`}
                >
                    <div
                        data-home-vehicle-search
                        className="w-full max-w-lg scroll-mt-4 xl:max-w-xl pointer-events-auto"
                    >
                        <DeferredSearchAuto initialFilters={searchAutoFilters} useNewSearch={useNewHomeSearch} />
                    </div>
                </div>
            </div>

            <div className="md:hidden px-4 mt-4">
                <div data-home-vehicle-search className="max-w-xl mx-auto scroll-mt-4">
                    <DeferredSearchAuto initialFilters={searchAutoFilters} useNewSearch={useNewHomeSearch} />
                </div>
            </div>

            <div className="py-6 sm:py-10 lg:py-12">
                <BrandGrid initialBrands={brands} />
            </div>

            {showDeferredSections ? (
                <DeferredLastMinute offers={offers} />
            ) : (
                <div className="min-h-[640px] bg-[#E1E7FF]" />
            )}

            <div className="max-w-7xl bg-transparent mx-auto px-4 py-6">
                <Link
                    href="/parco-veicoli?tipoVeicolo=Furgone"
                    className="block rounded-2xl bg-transparent overflow-hidden shadow-lg transition-transform hover:scale-[1.01]"
                    aria-label="Scopri i veicoli commerciali"
                >
                    <Image
                        src="/assets/Veicoli Commerciali Desktop Completo.webp"
                        alt="Veicoli commerciali Gruppo Piccirillo"
                        width={1600}
                        height={600}
                        className="hidden md:block w-full h-auto"
                    />
                    <Image
                        src="/assets/Veicoli Commerciali Mobile Completo.webp"
                        alt="Veicoli commerciali Gruppo Piccirillo"
                        width={800}
                        height={900}
                        className="md:hidden w-full h-auto"
                    />
                </Link>
            </div>

            {showDeferredSections ? (
                <DeferredDoveSiamoSlide locations={locations} />
            ) : (
                <div className="min-h-[520px]" />
            )}

            {showDeferredSections ? (
                <DeferredReview initialReviews={reviews} />
            ) : (
                <div className="min-h-[520px] bg-[#F4F4F4]" />
            )}
        </>
    );
}
